import api, { NO_RETRY } from './api';

export interface SupportConversation {
  id: number;
  user_id: number | null;
  anonymous_id: string | null;
  anonymous_name: string | null;
  status: 'open' | 'closed';
  admin_unread_count: number;
  user_unread_count: number;
  last_message_at: string | null;
  last_message_preview: string | null;
  sms_reply_phone: string | null;
  sms_reply_requested_at: string | null;
  created_at: string;
  updated_at: string;
  user_name?: string | null;
  user_email?: string | null;
}

export interface SupportMessage {
  id: number;
  conversation_id: number;
  sender_type: 'user' | 'admin' | 'system';
  sender_id: number | null;
  content: string | null;
  message_type: 'text' | 'image' | 'file';
  attachment_url?: string;
  attachment_name?: string;
  attachment_size?: number;
  created_at: string;
  read_at?: string | null;
}

export interface UploadResult {
  attachment_url: string;
  attachment_name: string;
  attachment_size: number;
  message_type: 'image' | 'file';
}

export interface UploadUrlResult {
  upload_url: string;
  method: 'PUT';
  headers: Record<string, string>;
  attachment_url: string;
  attachment_name: string;
  content_type: string;
  message_type: 'image' | 'file';
  expires_in: number;
}

export interface AttachmentLinkResult {
  url: string;
  expires_in: number;
}

export interface SupportUnreadCount {
  count: number;
}

const BASE = '/api/support-chat';

export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

// Blocklist: dangerous executable/script extensions
const BLOCKED_EXTENSIONS = new Set([
  '.exe', '.bat', '.cmd', '.com', '.msi', '.scr', '.pif',
  '.vbs', '.vbe', '.wsf', '.wsh', '.ps1', '.psm1',
  '.dll', '.sys', '.drv', '.cpl',
  '.lnk', '.url', '.reg', '.inf', '.hta',
  '.jar', '.sh', '.bin', '.app',
  // Active content: runs script if opened from the app origin (e.g. a blob: URL).
  '.html', '.htm', '.xhtml', '.xht', '.shtml', '.mht', '.mhtml',
  '.svg', '.svgz', '.xml', '.xsl', '.js', '.mjs',
]);

// Legacy multipart upload path (server proxies bytes) stays capped at 10 MB.
export const LEGACY_MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024; // 10 MB
// Direct-to-R2 uploads bypass the server, so the limit becomes 2 GB.
export const MAX_ATTACHMENT_SIZE = 2_000_000_000; // 2 GB

function getFileExtension(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot >= 0 ? filename.slice(dot).toLowerCase() : '';
}

export function isAllowedFile(file: File): boolean {
  if (file.size <= 0 || file.size > MAX_ATTACHMENT_SIZE) return false;
  const ext = getFileExtension(file.name);
  return ext !== '' && !BLOCKED_EXTENSIONS.has(ext);
}

export function isImageType(type: string): boolean {
  return ALLOWED_IMAGE_TYPES.includes(type);
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function getAttachmentUrl(path: string, conversationId: number, anonymousId?: string): string {
  let url = `${BASE}/attachment?path=${encodeURIComponent(path)}&conversation_id=${conversationId}`;
  if (anonymousId) url += `&anonymous_id=${encodeURIComponent(anonymousId)}`;
  return url;
}

// Direct-to-R2 uploads are stored under an 'r2/' key prefix; legacy uploads
// keep their original (server-local) path shape.
export function isDirectAttachmentPath(path: string): boolean {
  return path.startsWith('r2/');
}

export const supportChatApi = {
  createOrGetConversation: (data: { anonymous_id?: string; anonymous_name?: string }) =>
    api.post<SupportConversation>(`${BASE}/conversations`, data),

  listConversations: (status?: string, signal?: AbortSignal) =>
    api.get<SupportConversation[]>(`${BASE}/conversations`, { params: status ? { status } : {}, signal }),

  getMessages: (conversationId: number, anonymousId?: string, signal?: AbortSignal) =>
    api.get<SupportMessage[]>(`${BASE}/conversations/${conversationId}/messages`, {
      params: anonymousId ? { anonymous_id: anonymousId } : {},
      signal,
    }),

  getConversationUnreadCount: (conversationId: number, anonymousId?: string) =>
    api.get<SupportUnreadCount>(`${BASE}/conversations/${conversationId}/unread-count`, {
      params: anonymousId ? { anonymous_id: anonymousId } : {},
    }),

  sendMessage: (conversationId: number, data: {
    content?: string;
    sender_type: 'user' | 'admin';
    anonymous_id?: string;
    message_type?: 'text' | 'image' | 'file';
    attachment_url?: string;
    attachment_name?: string;
    attachment_size?: number;
  }) => api.post<SupportMessage>(`${BASE}/conversations/${conversationId}/messages`, data, NO_RETRY),

  uploadAttachment: (conversationId: number, file: File, senderType: 'user' | 'admin', anonymousId?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('sender_type', senderType);
    if (anonymousId) formData.append('anonymous_id', anonymousId);
    return api.post<UploadResult>(`${BASE}/conversations/${conversationId}/upload`, formData, {
      // Clear the shared JSON default so the browser can set the multipart boundary.
      headers: {
        'Content-Type': undefined,
      },
    });
  },

  // Requests a presigned R2 PUT URL. 503 direct_upload_unavailable means R2
  // isn't configured server-side; see uploadSupportAttachment for the fallback.
  getUploadUrl: (conversationId: number, data: {
    filename: string;
    size: number;
    sender_type: 'user' | 'admin';
    anonymous_id?: string;
  }) => api.post<UploadUrlResult>(`${BASE}/conversations/${conversationId}/upload-url`, data),

  // Resolves an 'r2/...' attachment path to a short-lived presigned GET URL.
  getAttachmentLink: (path: string, conversationId: number, anonymousId?: string) =>
    api.get<AttachmentLinkResult>(`${BASE}/attachment-link`, {
      params: {
        path,
        conversation_id: conversationId,
        ...(anonymousId ? { anonymous_id: anonymousId } : {}),
      },
    }),

  closeConversation: (conversationId: number) =>
    api.patch<SupportConversation>(`${BASE}/conversations/${conversationId}/close`),

  deleteConversation: (conversationId: number) =>
    api.delete(`${BASE}/conversations/${conversationId}`),

  markAsRead: (conversationId: number, data: {
    role: 'user' | 'admin';
    anonymous_id?: string;
  }) => api.post(`${BASE}/conversations/${conversationId}/read`, data),

  getUnreadCount: () =>
    api.get<SupportUnreadCount>(`${BASE}/unread-count`),

  requestSmsReply: (conversationId: number, data: {
    phone: string;
    message?: string;
    anonymous_id?: string;
  }) => api.post(`${BASE}/conversations/${conversationId}/sms-reply-request`, data, NO_RETRY),

  sendSmsReply: (conversationId: number, data: { message: string }) =>
    api.post(`${BASE}/conversations/${conversationId}/sms-reply-send`, data, NO_RETRY),
};

export const DIRECT_UPLOAD_UNAVAILABLE_MESSAGE =
  '현재 10MB를 넘는 파일은 보낼 수 없습니다. 잠시 후 다시 시도해주세요.';

function isDirectUploadUnavailableError(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { error?: string } } })?.response;
  return response?.status === 503 && response?.data?.error === 'direct_upload_unavailable';
}

// PUT the file straight to R2 with the exact headers the server signed. This is a
// cross-origin request to *.r2.cloudflarestorage.com, so it must NOT go through the
// shared `api` axios instance (that would add Authorization/JSON headers and break
// the signature/CORS). Plain XHR also gives us upload progress events.
function putFileToR2(
  uploadUrl: string,
  headers: Record<string, string>,
  file: File,
  onProgress?: (percent: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    Object.entries(headers).forEach(([key, value]) => xhr.setRequestHeader(key, value));

    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`R2 upload failed with status ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('R2 upload network error'));

    xhr.send(file);
  });
}

/**
 * Shared upload path for support-chat attachments: request a presigned R2 upload
 * URL, PUT the file directly to R2 (bytes never touch our server), and return the
 * same UploadResult shape the legacy multipart endpoint returns.
 *
 * If R2 isn't configured server-side (503 direct_upload_unavailable), falls back
 * to the legacy multipart upload for files within the old 10MB limit; larger
 * files throw with a Korean message the caller can surface directly.
 */
export async function uploadSupportAttachment(
  conversationId: number,
  file: File,
  senderType: 'user' | 'admin',
  anonymousId?: string,
  onProgress?: (percent: number) => void
): Promise<UploadResult> {
  try {
    const { data } = await supportChatApi.getUploadUrl(conversationId, {
      filename: file.name,
      size: file.size,
      sender_type: senderType,
      anonymous_id: anonymousId,
    });

    await putFileToR2(data.upload_url, data.headers, file, onProgress);

    return {
      attachment_url: data.attachment_url,
      attachment_name: data.attachment_name,
      attachment_size: file.size,
      message_type: data.message_type,
    };
  } catch (error) {
    if (isDirectUploadUnavailableError(error)) {
      if (file.size <= LEGACY_MAX_ATTACHMENT_SIZE) {
        const response = await supportChatApi.uploadAttachment(conversationId, file, senderType, anonymousId);
        return response.data;
      }
      throw new Error(DIRECT_UPLOAD_UNAVAILABLE_MESSAGE);
    }
    throw error;
  }
}

export interface AttachmentBatchProgress {
  /** 0-based index of the file being uploaded. */
  index: number;
  total: number;
  percent: number;
}

/** Thrown when a batch stops part-way; `remaining` starts with the failed file. */
export class AttachmentBatchError extends Error {
  readonly originalError: unknown;
  readonly remaining: File[];
  readonly sentCount: number;
  /** True when the caption text was not sent (it rides on the first file). */
  readonly contentUnsent: boolean;

  constructor(originalError: unknown, remaining: File[], sentCount: number, contentUnsent: boolean) {
    super(originalError instanceof Error ? originalError.message : 'Attachment batch failed');
    this.name = 'AttachmentBatchError';
    this.originalError = originalError;
    this.remaining = remaining;
    this.sentCount = sentCount;
    this.contentUnsent = contentUnsent;
  }
}

/**
 * Sends each file as its own message, one after another: upload, then send.
 * Optional caption text goes with the first file. Stops at the first
 * failure; files already sent stay sent.
 */
export async function sendAttachmentBatch({
  conversationId,
  files,
  senderType,
  anonymousId,
  content,
  onProgress,
  onSent,
}: {
  conversationId: number;
  files: File[];
  senderType: 'user' | 'admin';
  anonymousId?: string;
  content?: string;
  onProgress?: (progress: AttachmentBatchProgress) => void;
  onSent?: (message: SupportMessage) => void;
}): Promise<void> {
  for (let index = 0; index < files.length; index += 1) {
    const file = files[index];
    try {
      onProgress?.({ index, total: files.length, percent: 0 });
      const upload = await uploadSupportAttachment(
        conversationId,
        file,
        senderType,
        anonymousId,
        (percent) => onProgress?.({ index, total: files.length, percent })
      );
      const { data } = await supportChatApi.sendMessage(conversationId, {
        content: index === 0 && content ? content : undefined,
        sender_type: senderType,
        anonymous_id: anonymousId,
        message_type: upload.message_type,
        attachment_url: upload.attachment_url,
        attachment_name: upload.attachment_name,
        attachment_size: upload.attachment_size,
      });
      onSent?.(data);
    } catch (error) {
      throw new AttachmentBatchError(error, files.slice(index), index, index === 0 && Boolean(content));
    }
  }
}

export const UPLOAD_RATE_LIMITED_MESSAGE =
  '파일을 너무 빠르게 보내고 있습니다. 잠시 후 남은 파일을 다시 보내 주세요.';

/** User-facing reason for a failed attachment send. */
export function attachmentFailureMessage(error: unknown): string {
  if (error instanceof Error && error.message === DIRECT_UPLOAD_UNAVAILABLE_MESSAGE) return error.message;
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 429) return UPLOAD_RATE_LIMITED_MESSAGE;
  return '파일 전송에 실패했습니다. 다시 시도해 주세요.';
}

export function formatBatchProgress({ index, total, percent }: AttachmentBatchProgress): string {
  const position = total > 1 ? ` (${index + 1}/${total})` : '';
  return percent > 0 ? `업로드 중${position} ${percent}%` : `파일 업로드 중${position}...`;
}

